import { Template, LiquidError } from "liquidjs";
import mapValues from "lodash/mapValues";

import { makeActBlueRefcodeTransformer } from "@stw/extract-urls";

import { analyzeTemplate, TemplateAnalytics } from "./analyzer";
import engine, { ALLOWED_FILTERS, ALLOWED_TAGS } from "./engine";

export const LIQUID_VARIABLE_NAME_MATCHER = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

export type LiquidTemplateParseResult =
  | { ok: true; template: LiquidTemplate }
  | { ok: false; error: string };

export type LiquidTemplateRenderResult =
  | { ok: true; result: string }
  | { ok: false; error: string };

export class StrictStringForLiquid {
  private name: string;
  private value: string;

  constructor(name: string, value: string) {
    this.name = name;
    this.value = value;
  }

  toString(): string {
    return this.value;
  }

  valueOf(): number {
    const num = Number(this.value);
    if (isNaN(num)) {
      throw new Error(
        `expected ${this.name} to be a number, but it was: "${this.value}"`
      );
    }

    return num;
  }

  equals(other: unknown): boolean {
    return this.value === String(other);
  }

  gt(other: unknown): boolean {
    return this.valueOf() > Number(other);
  }

  geq(other: unknown): boolean {
    return this.valueOf() >= Number(other);
  }

  lt(other: unknown): boolean {
    return this.valueOf() < Number(other);
  }

  leq(other: unknown): boolean {
    return this.valueOf() <= Number(other);
  }

  indexOf(other: unknown): number {
    return this.value.indexOf(String(other));
  }
}

export default class LiquidTemplate {
  public readonly templateString: string;
  private readonly template: Template[];
  private templateAnalysis?: TemplateAnalytics;

  constructor(templateString: string, template: Template[]) {
    this.templateString = templateString;
    this.template = template;
  }

  static fromString(
    templateString: string,
    { validateVariables }: { validateVariables?: string[] } = {}
  ): LiquidTemplateParseResult {
    let template: LiquidTemplate;
    try {
      template = new LiquidTemplate(
        templateString,
        engine.parse(templateString)
      );
    } catch (error) {
      if (error instanceof LiquidError) {
        return { ok: false, error: error.message };
      }

      throw error;
    }

    // Tag validation is handled automatically during the parse step. But
    // filter validation isn't done until we actually try to render the
    // template. So we do that validation upfront here.
    const filtersInUse = template.getFiltersInUse();
    for (const filter of filtersInUse) {
      if (!ALLOWED_FILTERS.includes(filter)) {
        return {
          ok: false,
          error: `filter "${filter}" not found`,
        };
      }
    }

    if (validateVariables) {
      const requiredVariables = template.getRequiredVariables();
      for (const variable of requiredVariables) {
        if (!validateVariables.includes(variable)) {
          return {
            ok: false,
            error: `variable "${variable}" not found`,
          };
        }
      }
    }

    return { ok: true, template };
  }

  render(
    data: Record<string, string>,
    {
      refcode2w,
      autoAddRefcodesToABLinks,
    }: {
      refcode2w: string;
      autoAddRefcodesToABLinks: boolean;
    }
  ): LiquidTemplateRenderResult {
    const transforms = autoAddRefcodesToABLinks
      ? [makeActBlueRefcodeTransformer(refcode2w)]
      : [];

    const wrappedData: Record<string, null | StrictStringForLiquid> = mapValues(
      data,
      (value, key) => {
        if (String(value).trim() === "") {
          return null;
        } else {
          return new StrictStringForLiquid(key, value);
        }
      }
    );

    try {
      const renderedString = engine
        .renderSync(this.template, wrappedData, {
          globals: {
            refcode2w,
          },
        })
        .trim();
      return {
        ok: true,
        result: this.postRenderTransforms(renderedString, transforms),
      };
    } catch (e) {
      if (e instanceof Error) {
        return { ok: false, error: e.message };
      } else {
        throw e;
      }
    }
  }

  private getTemplateAnalysis(): TemplateAnalytics {
    if (!this.templateAnalysis) {
      this.templateAnalysis = analyzeTemplate(this.template);
    }
    return this.templateAnalysis;
  }

  private postRenderTransforms(
    templateString: string,
    transforms: ((templateStr: string) => string)[]
  ): string {
    return transforms.reduce((templateStr, transformer) => {
      return transformer(templateStr);
    }, templateString);
  }

  getRequiredVariables(): string[] {
    const analysis = this.getTemplateAnalysis();
    return Array.from(analysis.variablesInUse).filter(
      v => !analysis.variablesAssignedTo.has(v) && v !== "refcode2w"
    );
  }

  getRequiredAndAssignedVariables(): string[] {
    return Array.from(this.getTemplateAnalysis().variablesInUse).filter(
      v => v !== "refcode2w"
    );
  }

  getTagsInUse(): string[] {
    return Array.from(this.getTemplateAnalysis().tagsInUse);
  }

  getFiltersInUse(): string[] {
    return Array.from(this.getTemplateAnalysis().filtersInUse);
  }
}

export { ALLOWED_FILTERS, ALLOWED_TAGS };
